import { makeAutoObservable } from 'mobx';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { v4 as uuidv4 } from 'uuid';
import {
  buildMarketDataCandleKeys,
  buildMarketDataKey,
} from './utils/marketDataRouting';

export class PriceDataStore {
  root;
  priceStream;

  trades = {};
  lastTrade = {};
  summaries = {};
  lastQuote = {};
  timeSales = {};
  candles = {};

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.listenForPriceMessages();
  }
  messageMap = {
    trade: (msg) => this.handleTrade(msg),
    tradex: (msg) => this.handleTrade(msg),
    timesale: (msg) => this.handleTimesale(msg),
    summary: (msg) => this.handleSummary(msg),
    quote: (msg) => this.handleQuote(msg),
    candle: (msg) => this.handleCandle(msg),
  };

  listenForPriceMessages = () => {
    this.priceStream = groupByFunction(
      'messageType',
      'stream',
      this.root.websocketSubject
    );
    this.priceStream.subscribe((msg) => {
      const handler = this.messageMap[msg.type];
      if (handler) {
        handler(msg);
      }
    });
    groupByFunction(
      'messageType',
      'removeSymbol',
      this.root.websocketSubject
    ).subscribe((msg) => {
      this.removeSymbol(msg);
    });
  };
  
  removeSymbol = (msg) => {
    const keys = [
      buildMarketDataKey({
        symbol: msg.symbol,
        dataSource: msg.datasource,
        brokerage: msg.brokerage,
      }),
      buildMarketDataKey({ symbol: msg.symbol, from: msg.datasource }),
      buildMarketDataKey({ symbol: msg.symbol, from: msg.brokerage }),
    ].filter(Boolean);

    keys.forEach((hash) => {
      if (this.trades[hash]) delete this.trades[hash];
      if (this.summaries[hash]) delete this.summaries[hash];
      if (this.lastTrade[hash]) delete this.lastTrade[hash];
      if (this.lastQuote[hash]) delete this.lastQuote[hash];
      if (this.timeSales[hash]) delete this.timeSales[hash];
    });
    this.removeCandleHashes(keys);
  };

  updateHashes = (msg, updater) => {
    const keys = [
      buildMarketDataKey({
        symbol: msg.symbol,
        dataSource: msg.dataSource || msg.datasource,
        brokerage: msg.brokerage || msg.from,
      }),
      buildMarketDataKey({ symbol: msg.symbol, from: msg.from }),
      buildMarketDataKey({
        symbol: msg.symbol,
        from: msg.dataSource || msg.datasource,
      }),
    ].filter(Boolean);

    [...new Set(keys)].forEach((hash) => updater(hash));
  };

  normalizeCandleDuration = (msg) => {
    const explicitDuration = Number(msg.candleDuration);
    if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
      return explicitDuration;
    }

    const streamDuration = Number(msg.duration);
    if (Number.isFinite(streamDuration) && streamDuration > 0) {
      return streamDuration;
    }

    return msg.duration ?? msg.candleDuration ?? null;
  };

  buildCandleHashes = (msg) =>
    buildMarketDataCandleKeys({
      symbol: msg.symbol,
      dataSource: msg.dataSource || msg.datasource,
      brokerage: msg.brokerage || msg.from,
      timeUnit: msg.timeUnit,
      candleDuration: this.normalizeCandleDuration(msg),
      includeRouteFallback: true,
    });

  removeCandleHashes = (routeKeys) => {
    routeKeys.forEach((hash) => {
      if (this.candles[hash]) delete this.candles[hash];
    });

    const routePrefixes = routeKeys.map((hash) => `${hash}|`);
    Object.keys(this.candles).forEach((hash) => {
      if (routePrefixes.some((prefix) => hash.startsWith(prefix))) {
        delete this.candles[hash];
      }
    });
  };

  normalizeTimeStamp = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  shouldReplaceQuoteSide = (currentValue, currentTimeStamp, nextValue, nextTimeStamp) => {
    const normalizedCurrent = this.normalizeTimeStamp(currentTimeStamp);
    const normalizedNext = this.normalizeTimeStamp(nextTimeStamp);
    const currentNumber = Number(currentValue);
    const nextNumber = Number(nextValue);

    if (normalizedNext === null) {
      if (normalizedCurrent === null) {
        return true;
      }

      return currentNumber === 0 && Number.isFinite(nextNumber) && nextNumber !== 0;
    }
    if (normalizedCurrent === null) {
      return true;
    }

    return normalizedNext >= normalizedCurrent;
  };

  shouldReplaceTrade = (hash, nextTimeStamp) => {
    const normalizedCurrent = this.normalizeTimeStamp(
      this.trades[hash]?.timeStamp
    );
    const normalizedNext = this.normalizeTimeStamp(nextTimeStamp);

    if (normalizedNext === null) {
      return true;
    }
    if (normalizedCurrent === null) {
      return true;
    }

    return normalizedNext >= normalizedCurrent;
  };

  mergeQuote = (hash, { bid, ask, bidTimeStamp, askTimeStamp }) => {
    const existingQuote = this.lastQuote[hash] ?? {};
    const nextQuote = { ...existingQuote };

    if (
      bid !== undefined &&
      bid !== null &&
      this.shouldReplaceQuoteSide(
        existingQuote.bid,
        existingQuote.bidTimeStamp,
        bid,
        bidTimeStamp
      )
    ) {
      nextQuote.bid = Number(bid);
      nextQuote.bidTimeStamp = this.normalizeTimeStamp(bidTimeStamp);
    }

    if (
      ask !== undefined &&
      ask !== null &&
      this.shouldReplaceQuoteSide(
        existingQuote.ask,
        existingQuote.askTimeStamp,
        ask,
        askTimeStamp
      )
    ) {
      nextQuote.ask = Number(ask);
      nextQuote.askTimeStamp = this.normalizeTimeStamp(askTimeStamp);
    }

    if (Object.keys(nextQuote).length > 0) {
      this.lastQuote[hash] = nextQuote;
    }
  };

  handleTrade = (msg) => {
    this.updateHashes(msg, (hash) => {
      if (!this.shouldReplaceTrade(hash, msg.date)) {
        return;
      }
      this.lastTrade[hash] = msg;
      if (this.trades[hash]) {
        this.trades[hash].price = msg.price;
        this.trades[hash].timeStamp = msg.date;
        this.trades[hash].volume = msg.size;
      } else {
        this.trades[hash] = {
          price: msg.price,
          timeStamp: msg.date,
          volume: msg.size,
        };
      }
    });
  };

  handleTimesale = (msg) => {
    this.updateHashes(msg, (hash) => {
      if (!this.shouldReplaceTrade(hash, msg.date)) {
        return;
      }
      this.lastTrade[hash] = msg;
      if (this.trades[hash]) {
        this.trades[hash].price = msg.last;
        this.trades[hash].timeStamp = msg.date;
        this.trades[hash].volume = msg.size;
      } else {
        this.trades[hash] = {
          price: msg.last,
          timeStamp: msg.date,
          volume: msg.size,
        };
      }
      this.mergeQuote(hash, {
        bid: msg.bid,
        ask: msg.ask,
        bidTimeStamp: msg.date,
        askTimeStamp: msg.date,
      });
      if (this.timeSales[hash]) {
        this.timeSales[hash].collection.unshift({
          bid: msg.bid,
          ask: msg.ask,
          last: msg.last,
          volume: Number(msg.size),
          timeStamp: this.normalizeTimeStamp(msg.date),
          id: uuidv4(),
        });

        if (this.timeSales[hash].collection.length > 50) {
          this.timeSales[hash].collection.pop();
        }
      } else {
        this.timeSales[hash] = {
          collection: [
            {
              bid: msg.bid,
              ask: msg.ask,
              last: msg.last,
              volume: Number(msg.size),
              timeStamp: this.normalizeTimeStamp(msg.date),
              id: uuidv4(),
            },
          ],
        };
      }
    });
  };
  handleQuote = (msg) => {
    this.updateHashes(msg, (hash) => {
      this.mergeQuote(hash, {
        bid: msg.bid,
        ask: msg.ask,
        bidTimeStamp: msg.biddate ?? msg.time ?? msg.date,
        askTimeStamp: msg.askdate ?? msg.time ?? msg.date,
      });
    });
  };
  handleSummary = (msg) => {
    this.updateHashes(msg, (hash) => {
      this.summaries[hash] = msg;
    });
  };

  handleCandle = (msg) => {
    this.buildCandleHashes(msg).forEach((hash) => {
      const nextTimeStamp = Number(msg.timeStamp);
      const currentTimeStamp = Number(this.candles[hash]?.timeStamp);
      if (
        Number.isFinite(currentTimeStamp) &&
        Number.isFinite(nextTimeStamp) &&
        nextTimeStamp < currentTimeStamp
      ) {
        return;
      }
      this.candles[hash] = {
        ...msg,
        timeStamp: nextTimeStamp,
        open: Number(msg.open),
        high: Number(msg.high),
        low: Number(msg.low),
        close: Number(msg.close),
        volume: Number(msg.volume ?? 0),
      };
    });
  };

  removeFromTradeStore = () => {};
}
